import { useEffect, useState } from 'react';

import { Card, SegmentedControl } from '../../components/index.ts';
import { changeTheme, getThemePreference, onThemeChange, resolveTheme } from '../../lib/theme.ts';
import type { ResolvedTheme, ThemePreference } from '../../lib/theme.ts';

const SEGMENTS = [
  { value: 'system', label: 'System' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
] as const satisfies readonly { value: ThemePreference; label: string }[];

/**
 * The theme override. Stored per device, not per account — "dark on my phone,
 * light on the ward PC" is a device-shaped preference. Following the operating
 * system is the default and stays an option, because most people set this once
 * at the OS level and never think about it again.
 */
export function AppearanceSection() {
  const [preference, setPreference] = useState<ThemePreference>(() => getThemePreference());
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolveTheme(getThemePreference()));

  // Keeps the description honest while "System" is selected and the OS flips.
  useEffect(() => onThemeChange(setResolved), []);

  function handleChange(next: ThemePreference): void {
    setPreference(next);
    setResolved(changeTheme(next));
  }

  return (
    <Card title="Appearance">
      <SegmentedControl
        label="Colour theme"
        segments={SEGMENTS}
        value={preference}
        onChange={handleChange}
      />

      <p className="pt-3 text-sm text-muted">
        {preference === 'system'
          ? `Following this device’s setting, which is currently ${resolved}.`
          : `Always ${preference}, whatever this device is set to.`}
      </p>
    </Card>
  );
}
